/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useState
} from 'react';
import {
    useLocation, useNavigate, useParams, useSearchParams
} from 'react-router-dom';
import {
    Context
} from 'shared/context/GlobalState';
import {
    Radio, MessageCircle, Mail, Phone, Globe, Slack
} from 'lucide-react';
import {
    channelService
} from 'shared/api/services/BEX/channel.service';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from 'shared/components/ui/alert-dialog';
import AuthenticateEmail from 'builders/builderChannel/components/AuthenticateEmail';
import DSResourceList, { PAGE_SIZE_LIST } from './DSResourceList';

/**
 * DSChannelsList — DS-themed list for Library → Channels.
 * Replaces the legacy `<Channel />` builder which carried a marketing hero
 * + a different card style. The data layer (channelService.getChannels)
 * stays. Click → routes to /de-studio/library/<channelId> which mounts the
 * legacy ChannelDetails editor (route already wired in App.js).
 */
const KIND_ICON = {
    web: Globe,
    webchat: Globe,
    whatsapp: MessageCircle,
    messenger: MessageCircle,
    slack: Slack,
    teams: MessageCircle,
    email: Mail,
    sms: Phone,
    call: Phone,
    voice: Phone,
};

export default function DSChannelsList() {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        oragID, subOragID
    } = useParams();
    const { addNewNotifcation } = useContext(Context);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    // When set, fetch the full set so DSResourceList filters across all pages.
    const [search, setSearch] = useState('');
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const authSuccess = searchParams.get('email-auth-success');
        const statusCode = searchParams.get('statusCode');
        const errorMessage = searchParams.get('errorMessage');
        if (authSuccess === 'true') {
            // Reopen the create-channel modal so it can finish setup after the
            // Outlook redirect. Do NOT strip the email-auth-success / email params
            // here: the reopened modal (EmailSetup) reads them to load the stashed
            // Outlook tokens via get-email-auth-temp. Stripping them synchronously
            // drops the just-completed sign-in and the modal falls back to the
            // "Authenticate with Outlook" screen. EmailSetup clears the params
            // itself once it has consumed the temp auth.
            setCreateOpen(true);
        } else if (authSuccess === 'false') {
            if (statusCode === '409') {
                addNewNotifcation('This email address is already connected to a channel.', 'danger');
            } else if (errorMessage) {
                addNewNotifcation(`Email setup failed: ${errorMessage}`, 'danger');
            } else if (statusCode === '403') {
                addNewNotifcation('Email authentication failed. Please check your Outlook configuration.', 'danger');
            } else {
                addNewNotifcation('Email authentication failed. Please try again.', 'danger');
            }
            // Only the failure path cleans the URL here; the success path leaves the
            // params in place for the modal to consume.
            navigate(location.pathname + '?tab=channels', { replace: true });
        }
    }, []);

    function fetchPage(page) {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        const searching = search.length > 0;
        channelService.getChannels(oragID, subOragID, searching ? 1 : page, searching ? 1000 : PAGE_SIZE_LIST)
            .then((res) => {
                const payload = res?.data ?? res;
                if (payload && typeof payload === 'object' && 'data' in payload && 'pagination' in payload) {
                    setItems(Array.isArray(payload.data) ? payload.data : []);
                    setTotalPages(payload.pagination.totalPages ?? 1);
                    setTotalItems(payload.pagination.totalItems ?? payload.data.length);
                } else {
                    const arr = Array.isArray(payload) ? payload : [];
                    setItems(arr);
                    setTotalPages(1);
                    setTotalItems(arr.length);
                }
            })
            .catch((err) => setError(err?.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }

    function reload() { fetchPage(currentPage); }
    useEffect(() => { setCurrentPage(1); }, [oragID, subOragID]);
    useEffect(() => { fetchPage(currentPage); }, [oragID, subOragID, currentPage, search]);

    function openItem(c) {
        navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/channels/${c._id}`);
    }

    async function handleDelete(c) {
        try {
            await channelService.deleteChannel({
                organizationId: oragID,
                subOrganizationId: subOragID,
                channelId: c._id,
            });
            setConfirmDelete(null);
            reload();
        } catch (err) {
            alert('Delete failed: ' + (err?.response?.data?.message || err?.message || String(err)));
        }
    }

    return (
        <>
            <DSResourceList
                items={items}
                loading={loading}
                error={error}
                getKey={(it) => it._id}
                getCard={(it) => {
                    const rawKind = (it.type || it.kind || it.channel_type || '').toLowerCase().replace(/\s+/g, '');
                    const Icon = KIND_ICON[rawKind] || Radio;
                    const displayType = it.type || it.kind || 'Channel';
                    return {
                        icon: Icon,
                        tone: 'violet',
                        title: it.topic || it.name || it._id,
                        subtitle: displayType.toUpperCase(),
                        description: it.description || '',
                        version: it.version ? `v${it.version}` : null,
                        badge: it.status ? {
                            tone: it.status === 'active' ? 'success' : 'neutral',
                            label: it.status === 'active' ? 'Live' : it.status
                        } : undefined,
                        stats: [],
                        idLine: it._id,
                        createdBy: it.createdBy || null,
                        createdAt: it.created || null,
                        searchTerms: [rawKind, it.topic, it.name, it.description]
                    };
                }}
                onPick={openItem}
                onEdit={openItem}
                onDelete={(c) => setConfirmDelete(c)}
                onCreate={() => setCreateOpen(true)}
                newLabel="New channel"
                searchPlaceholder="Search channels…"
                emptyTitle="No channels yet"
                emptyDescription="Connect your first channel — your assistants reach customers through these."
                emptyIcon={Radio}
                serverPage={search ? undefined : currentPage}
                serverTotalPages={search ? undefined : totalPages}
                onServerPage={search ? undefined : setCurrentPage}
                serverItemCount={search ? undefined : totalItems}
                onSearchChange={(q) => { setSearch(q); setCurrentPage(1); }}
            />

            <AuthenticateEmail
                isOpen={createOpen}
                setOpenEmailModal={setCreateOpen}
                setEmailChannels={() => { setCreateOpen(false); reload(); }}
            />

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this channel?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <b>{confirmDelete?.topic || confirmDelete?.name || confirmDelete?._id}</b> will be permanently removed.
                            Any active connections routed through it will stop working immediately.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => handleDelete(confirmDelete)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
